import { useEffect, useMemo, useRef, useState } from "react";

import { DownloadIcon, IconButton } from "../../components/ui";
import type { AgentChatToolCall, AgentChatTurnState } from "../api/e2e";
import {
  resolveWidgetTheme,
  svgRampStyles,
  themeDeclarations,
  widgetHostStyles,
  type WidgetTheme,
} from "./AgentChatWidgetDesign";

export const SHOW_WIDGET_TOOL_NAME = "show_widget";

const MAX_WIDGET_CODE_CHARS = 50_000;
const MAX_WIDGET_LOADING_MESSAGE_CHARS = 80;
const MAX_WIDGET_TITLE_CHARS = 80;
const MIN_WIDGET_HEIGHT = 1;
const MAX_WIDGET_HEIGHT = 720;
const DEFAULT_WIDGET_HEIGHT = 280;
const WIDGET_MESSAGE_SOURCE = "fdr-e2e-chat-widget";
const WIDGET_TITLE_PATTERN = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/u;
const HTML_DOCUMENT_WRAPPER_PATTERN =
  /<!doctype\b|<\s*\/?\s*(?:html|head|body)\b/iu;
const SVG_SCRIPT_PATTERN = /<\s*script\b|\bon[a-z]+\s*=|javascript\s*:/iu;

type WidgetFormat = "html" | "svg";

export interface AgentChatWidgetSpec {
  widgetCode: string;
  title: string;
  loadingMessages: string[];
  format: WidgetFormat;
}

let loadedTablerIconCss: string | null = null;
let widgetAssetsPromise: Promise<string> | null = null;

export function preloadAgentChatWidgetAssets(): Promise<string> {
  widgetAssetsPromise ??= import("./AgentChatWidgetAssets")
    .then((assets) => {
      loadedTablerIconCss = assets.tablerIconCss;
      return assets.tablerIconCss;
    })
    .catch(() => {
      loadedTablerIconCss = "";
      return "";
    });
  return widgetAssetsPromise;
}

function record(value: unknown): Record<string, unknown> | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

export function parseAgentChatWidget(
  toolCall: AgentChatToolCall,
): AgentChatWidgetSpec | null {
  if (toolCall.name !== SHOW_WIDGET_TOOL_NAME) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(toolCall.arguments);
  } catch {
    return null;
  }
  const argumentsRecord = record(parsed);
  if (!argumentsRecord) return null;
  const widgetCode = argumentsRecord.widget_code;
  const title = argumentsRecord.title;
  const loadingMessages = argumentsRecord.loading_messages;
  if (
    typeof widgetCode !== "string" ||
    widgetCode.trim().length === 0 ||
    widgetCode.length > MAX_WIDGET_CODE_CHARS ||
    typeof title !== "string" ||
    title.length > MAX_WIDGET_TITLE_CHARS ||
    !WIDGET_TITLE_PATTERN.test(title) ||
    !Array.isArray(loadingMessages) ||
    loadingMessages.length < 1 ||
    loadingMessages.length > 4 ||
    !loadingMessages.every(
      (message) =>
        typeof message === "string" &&
        message.trim().length > 0 &&
        message.length <= MAX_WIDGET_LOADING_MESSAGE_CHARS,
    )
  ) {
    return null;
  }

  const normalized = widgetCode.trimStart();
  const format: WidgetFormat = /^<svg(?:\s|>)/iu.test(normalized)
    ? "svg"
    : "html";
  if (
    (format === "svg" && SVG_SCRIPT_PATTERN.test(widgetCode)) ||
    (format === "html" && HTML_DOCUMENT_WRAPPER_PATTERN.test(widgetCode))
  ) {
    return null;
  }
  return {
    widgetCode,
    title,
    loadingMessages: loadingMessages.map((message) => message.trim()),
    format,
  };
}

function widgetOutputError(
  output: string | null,
  spec: AgentChatWidgetSpec | null,
): string | null {
  if (output === null) return null;
  try {
    const payload = record(JSON.parse(output));
    if (payload && typeof payload.error === "string") return payload.error;
    if (
      !payload ||
      spec === null ||
      payload.status !== "ready" ||
      payload.title !== spec.title ||
      payload.format !== spec.format ||
      payload.code_chars !== spec.widgetCode.length
    ) {
      return "The widget renderer returned an invalid result.";
    }
    return null;
  } catch {
    return "The widget renderer returned an invalid result.";
  }
}

function createChannel(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  const values = new Uint32Array(4);
  globalThis.crypto?.getRandomValues?.(values);
  return Array.from(values, (value) =>
    value.toString(16).padStart(8, "0"),
  ).join("");
}

function htmlEscape(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export function buildWidgetDocument(
  spec: AgentChatWidgetSpec,
  channel: string,
  theme: WidgetTheme,
  tablerIconCss: string,
): string {
  const encodedChannel = JSON.stringify(channel);
  const encodedSource = JSON.stringify(WIDGET_MESSAGE_SOURCE);
  const dark = document.documentElement.dataset.theme === "dark";
  const colorScheme = dark ? "dark" : "light";
  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="referrer" content="no-referrer">
  <meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data: blob:; media-src data: blob:; font-src data:; style-src 'unsafe-inline'; script-src 'unsafe-inline'; connect-src 'none'; object-src 'none'; frame-src 'none'; child-src 'none'; worker-src 'none'; base-uri 'none'; form-action 'none'; navigate-to 'none'">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${htmlEscape(spec.title)}</title>
  <style>
    :root {
      color-scheme: ${colorScheme};
      ${themeDeclarations(theme)}
    }
    ${widgetHostStyles()}
    ${svgRampStyles(dark)}
    ${tablerIconCss}
  </style>
</head>
<body>
  <script>
    (() => {
      const channel = ${encodedChannel};
      const source = ${encodedSource};
      const bootstrap = document.currentScript;
      bootstrap?.remove();
      const activation = navigator.userActivation;
      const activationGetter = activation
        ? Object.getOwnPropertyDescriptor(Object.getPrototypeOf(activation), "isActive")?.get
        : undefined;
      const apply = Reflect.apply;
      const userIsActive = () => Boolean(
        activation && activationGetter && apply(activationGetter, activation, []),
      );
      const post = (type, payload = {}) => {
        window.parent.postMessage({ source, channel, type, ...payload }, "*");
      };
      Object.defineProperty(window, "sendPrompt", {
        configurable: false,
        enumerable: true,
        writable: false,
        value(text) {
          if (!userIsActive() || typeof text !== "string") return false;
          const normalized = text.trim();
          if (!normalized || normalized.length > 8000) return false;
          post("prompt", { text: normalized });
          return true;
        },
      });
      let lastHeight = 0;
      const reportHeight = () => {
        const body = document.body;
        if (!body) return;
        const bodyRect = body.getBoundingClientRect();
        const contentBottom = Array.from(body.children).reduce(
          (bottom, child) => Math.max(
            bottom,
            child.getBoundingClientRect().bottom - bodyRect.top,
          ),
          bodyRect.height,
        );
        const height = Math.max(1, Math.ceil(contentBottom));
        if (height !== lastHeight) {
          lastHeight = height;
          post("resize", { height });
        }
      };
      window.addEventListener("load", reportHeight, { once: true });
      window.addEventListener("resize", reportHeight);
      if (typeof ResizeObserver === "function") {
        new ResizeObserver(reportHeight).observe(document.body);
      } else {
        new MutationObserver(reportHeight).observe(document.body, {
          attributes: true,
          childList: true,
          subtree: true,
        });
      }
      requestAnimationFrame(reportHeight);
    })();
  </script>
  ${spec.widgetCode}
</body>
</html>`;
}

function downloadWidget(spec: AgentChatWidgetSpec): void {
  const extension = spec.format === "svg" ? "svg" : "html";
  const mediaType = spec.format === "svg" ? "image/svg+xml" : "text/html";
  const blob = new Blob([spec.widgetCode], {
    type: `${mediaType};charset=utf-8`,
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${spec.title}.${extension}`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export default function AgentChatWidget({
  toolCall,
  turnState,
  onSendPrompt,
}: {
  toolCall: AgentChatToolCall;
  turnState: AgentChatTurnState;
  onSendPrompt: (text: string) => void;
}) {
  const spec = useMemo(() => parseAgentChatWidget(toolCall), [toolCall]);
  const [channel] = useState(createChannel);
  const [theme, setTheme] = useState(resolveWidgetTheme);
  const iframe = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(DEFAULT_WIDGET_HEIGHT);
  const [loaded, setLoaded] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [tablerIconCss, setTablerIconCss] = useState(loadedTablerIconCss);
  const outputError = widgetOutputError(toolCall.output, spec);
  const active = turnState === "queued" || turnState === "running";
  const completed =
    spec !== null &&
    turnState === "succeeded" &&
    toolCall.output !== null &&
    outputError === null;
  const ready = completed && tablerIconCss !== null;
  const showLoading =
    spec !== null && (active || (completed && (!ready || !loaded)));
  const srcDoc = useMemo(
    () =>
      spec && tablerIconCss !== null
        ? buildWidgetDocument(spec, channel, theme, tablerIconCss)
        : "",
    [channel, spec, tablerIconCss, theme],
  );

  useEffect(() => {
    if (tablerIconCss !== null) return;
    let mounted = true;
    void preloadAgentChatWidgetAssets().then((css) => {
      if (mounted) setTablerIconCss(css);
    });
    return () => {
      mounted = false;
    };
  }, [tablerIconCss]);

  useEffect(() => {
    setLoaded(false);
    setHeight(DEFAULT_WIDGET_HEIGHT);
    setLoadingMessageIndex(0);
  }, [toolCall.call_id]);

  useEffect(() => {
    if (!showLoading || !spec || spec.loadingMessages.length < 2) return;
    const timer = window.setInterval(() => {
      setLoadingMessageIndex(
        (current) => (current + 1) % spec.loadingMessages.length,
      );
    }, 1_600);
    return () => window.clearInterval(timer);
  }, [showLoading, spec]);

  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(resolveWidgetTheme()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const receiveWidgetMessage = (event: MessageEvent<unknown>) => {
      if (event.source !== iframe.current?.contentWindow) return;
      const data = record(event.data);
      if (
        !data ||
        data.source !== WIDGET_MESSAGE_SOURCE ||
        data.channel !== channel
      ) {
        return;
      }
      if (data.type === "resize" && typeof data.height === "number") {
        setHeight(
          Math.min(
            MAX_WIDGET_HEIGHT,
            Math.max(MIN_WIDGET_HEIGHT, Math.ceil(data.height)),
          ),
        );
      }
      if (
        data.type === "prompt" &&
        typeof data.text === "string" &&
        data.text.trim().length > 0 &&
        data.text.length <= 8_000
      ) {
        onSendPrompt(data.text.trim());
      }
    };
    window.addEventListener("message", receiveWidgetMessage);
    return () => window.removeEventListener("message", receiveWidgetMessage);
  }, [channel, onSendPrompt]);

  const label = spec?.title.replaceAll("_", " ") ?? "Model visual";
  if (!ready && !showLoading) return null;

  return (
    <article className="agent-model-chat__widget" aria-label={label}>
      {ready && spec && (
        <div className="agent-model-chat__widget-actions">
          <IconButton
            type="button"
            tone="quiet"
            size="sm"
            label={`Download ${label}`}
            tooltip="Download visual"
            icon={<DownloadIcon />}
            onClick={() => downloadWidget(spec)}
          />
        </div>
      )}
      <div
        className={["agent-model-chat__widget-stage", ready ? "is-ready" : ""]
          .filter(Boolean)
          .join(" ")}
        style={{ minHeight: ready ? `${height}px` : undefined }}
      >
        {ready && spec && (
          <iframe
            ref={iframe}
            className={loaded ? "is-loaded" : undefined}
            title={label}
            sandbox="allow-scripts"
            referrerPolicy="no-referrer"
            srcDoc={srcDoc}
            style={{ height: `${height}px` }}
            onLoad={() => setLoaded(true)}
          />
        )}
        {showLoading && spec && (
          <div className="agent-model-chat__widget-loading" role="status">
            <span aria-hidden="true" />
            <p>{spec.loadingMessages[loadingMessageIndex]}</p>
          </div>
        )}
      </div>
    </article>
  );
}
